import { useEffect, useContext, useState } from "react";
import {
  Modal,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ToastContext } from "../../providers/ToastProvider";
import { DrawerContext } from "../../providers/DrawerProvider";
import { useForm } from "react-hook-form";
import { useTheme } from "@mui/material/styles";
import { validateField, getCurrency } from "../../common/globalFunctions";

const UpdateCostModal = ({ 
  open, 
  handleClose, 
  currentProduct, 
  stockTotalService, 
  setProducts
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { setShowToast, setToastMessage } = useContext(ToastContext);
  const { drawerOpen } = useContext(DrawerContext);
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      cost: "",
    },
  });
  const theme = useTheme();

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      const newCost = parseFloat(data.cost);
      
      if (newCost < 0) {
        setToastMessage([
          {
            severity: "error",
            primary: "El costo no puede ser negativo",
          },
        ]);
        setShowToast(true);
        return;
      }

      const payload = {
        productId: currentProduct.id,
        cost: newCost,
      };

      const res = await stockTotalService.updateCost(payload);

      if (!res.data.success) {
        setToastMessage([
          {
            severity: "error",
            primary: "Error al actualizar el costo del producto",
          },
        ]);
        setShowToast(true);
        return;
      }

      setProducts((oldProducts) => {
        return oldProducts.map(product => {
          if (product.id === currentProduct.id) {
            return {
              ...product,
              cost: newCost
            };
          }
          return product;
        });
      });

      setToastMessage([
        {
          severity: "success",
          primary: `Costo actualizado correctamente para ${currentProduct.reference}`,
        },
      ]);
      setShowToast(true);
      handleClose();
    } catch (error) {
      setToastMessage([
        {
          severity: "error",
          primary: "Error al intentar actualizar el costo",
        },
      ]);
      setShowToast(true);
    } finally {
      setIsLoading(false);
    }
  };

  const keyPress = (e) => {
    if (e.keyCode === 13) {
      handleSubmit(onSubmit)();
    }
  };

  useEffect(() => {
    if (open && currentProduct) {
      reset();
      setValue("cost", currentProduct.cost || "");
    }
  }, [reset, open, currentProduct, setValue]);

  const classes = {
    root: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      left: drawerOpen ? theme.drawerWidth : 0,

      "& .MuiModal-backdrop": {
        left: drawerOpen ? theme.drawerWidth : 0,
      },
    },
    paper: {
      width: "90%",
      maxWidth: 400,
      textAlign: "center",
      position: "relative",
      top: -50,
      pt: 3,
      pb: 5,
      px: 6,
    },
    actions: {
      display: "flex",
      justifyContent: "center",
      gap: 2,
    },
  };

  return (
    <Modal open={open} onClose={handleClose} sx={classes.root}>
      <Paper onKeyDown={keyPress} sx={classes.paper}>
        <Box
          component="form"
          autoComplete="off"
          noValidate
          onSubmit={handleSubmit(onSubmit)}
        >
          <Typography variant="h6" component="h3" mb={3}>
            <Box component={FontAwesomeIcon} icon="dollar-sign" mr={2} />
            Actualizar Costo
          </Typography>
          
          {currentProduct && (
            <Typography variant="body2" color="text.secondary" mb={2}>
              <strong>{currentProduct.reference}</strong>
              <br />
              {currentProduct.description}
              <br />
              Costo actual: {getCurrency(currentProduct.cost || 0)}
            </Typography>
          )}

          <TextField
            label="Nuevo costo"
            variant="filled"
            type="number"
            inputProps={{
              step: "0.01",
              min: "0"
            }}
            autoFocus
            autoComplete="off"
            disabled={isLoading}
            sx={{ width: "100%", mb: 3 }}
            {...register("cost", {
              required: true,
              min: 0,
              pattern: /^\d+(\.\d{1,2})?$/,
            })}
            error={errors.cost !== undefined}
            helperText={validateField(errors.cost)}
          />

          <Box sx={classes.actions}>
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading}
              sx={{ width: isLoading ? "100%" : "fit-content" }}
            >
              {isLoading ? (
                <>
                  Actualizando costo{" "}
                  <CircularProgress
                    sx={{ ml: 1 }}
                    thickness={3}
                    size={20}
                    color="secondary"
                  />
                </>
              ) : (
                "Aceptar"
              )}
            </Button>
            {!isLoading && (
              <Button variant="contained" color="error" onClick={handleClose}>
                Cancelar
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    </Modal>
  );
};

export default UpdateCostModal;